import {
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, HelpOutline } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";

export default ({
  actionParameters,
  setActionParameters,
  headers,
  existingRecord,
  setValidForm,
}: {
  actionParameters: any;
  setActionParameters: (p: any) => void;
  headers: Array<any>;
  existingRecord: boolean;
  setValidForm: (b: boolean) => void;
}) => {
  const urlRef = useRef<any>(null);
  
  // Initialize query parameters, body, and headers as arrays if not already set
  const [queryParams, setQueryParams] = useState<Array<{key: string, value: string}>>([]);
  const [bodyParams, setBodyParams] = useState<Array<{key: string, value: string}>>([]);
  const [headerParams, setHeaderParams] = useState<Array<{key: string, value: string}>>([]);
  
  // State for variable dropdown
  const [variableMenuAnchor, setVariableMenuAnchor] = useState<null | { element: HTMLElement, type: 'query' | 'body' | 'header', index: number }>(null);

  useEffect(() => {
    // Initialize all required fields with defaults
    const updates: any = {};
    let hasUpdates = false;
    
    if (!actionParameters.action) {
      updates.action = "update_rows";
      hasUpdates = true;
    }
    if (actionParameters.url === undefined) {
      updates.url = "";
      hasUpdates = true;
    }
    if (actionParameters.method === undefined) {
      updates.method = "POST";
      hasUpdates = true;
    }
    if (actionParameters.queryParams === undefined) {
      updates.queryParams = "";
      hasUpdates = true;
    }
    if (actionParameters.body === undefined) {
      updates.body = "{}";
      hasUpdates = true;
    }
    if (actionParameters.headers === undefined) {
      updates.headers = '{"Content-Type": "application/json"}';
      hasUpdates = true;
    }
    
    if (hasUpdates) {
      setActionParameters({
        ...actionParameters,
        ...updates
      });
    }
    
    // Initialize query params from string if needed
    if (actionParameters.queryParams && typeof actionParameters.queryParams === 'string' && queryParams.length === 0) {
      const params = new URLSearchParams(actionParameters.queryParams);
      const paramsArray: Array<{key: string, value: string}> = [];
      params.forEach((value, key) => {
        paramsArray.push({ key, value });
      });
      setQueryParams(paramsArray.length > 0 ? paramsArray : [{ key: '', value: '' }]);
    } else if (queryParams.length === 0) {
      setQueryParams([{ key: '', value: '' }]);
    }
    
    // Initialize body params from JSON string if needed
    if (actionParameters.body && typeof actionParameters.body === 'string' && bodyParams.length === 0) {
      try {
        const bodyObj = JSON.parse(actionParameters.body);
        const paramsArray: Array<{key: string, value: string}> = [];
        Object.entries(bodyObj).forEach(([key, value]) => {
          paramsArray.push({ key, value: String(value) });
        });
        setBodyParams(paramsArray.length > 0 ? paramsArray : [{ key: '', value: '' }]);
      } catch {
        setBodyParams([{ key: '', value: '' }]);
      }
    } else if (bodyParams.length === 0) {
      setBodyParams([{ key: '', value: '' }]);
    }
    
    // Initialize header params from JSON string if needed
    if (actionParameters.headers && typeof actionParameters.headers === 'string' && headerParams.length === 0) {
      try {
        const headersObj = JSON.parse(actionParameters.headers);
        const paramsArray: Array<{key: string, value: string}> = [];
        Object.entries(headersObj).forEach(([key, value]) => {
          paramsArray.push({ key, value: String(value) });
        });
        setHeaderParams(paramsArray.length > 0 ? paramsArray : [{ key: 'Content-Type', value: 'application/json' }]);
      } catch {
        setHeaderParams([{ key: 'Content-Type', value: 'application/json' }]);
      }
    } else if (headerParams.length === 0) {
      setHeaderParams([{ key: 'Content-Type', value: 'application/json' }]);
    }
    
    setValidForm(
      actionParameters.url !== undefined && 
      actionParameters.url !== "" &&
      actionParameters.method !== undefined && 
      actionParameters.headers !== undefined
    );
  }, [actionParameters, queryParams.length, bodyParams.length, headerParams.length]);


  const handleUrlInputChange = (value: string) => {
    setActionParameters({ ...actionParameters, url: value });
  };
  
  const insertVariable = (variable: string) => {
    if (!variableMenuAnchor) return;
    
    const { type, index } = variableMenuAnchor;
    let params, setParams, updateString;
    
    if (type === 'query') {
      params = queryParams;
      setParams = setQueryParams;
      updateString = updateQueryParamsString;
    } else if (type === 'body') {
      params = bodyParams;
      setParams = setBodyParams;
      updateString = updateBodyParamsString;
    } else {
      params = headerParams;
      setParams = setHeaderParams;
      updateString = updateHeaderParamsString;
    }
    
    const newParams = [...params];
    // Remove the "/" and add the variable
    const currentValue = newParams[index].value;
    newParams[index].value = currentValue.slice(0, -1) + `{{${variable}}}`;
    setParams(newParams);
    updateString(newParams);
    setVariableMenuAnchor(null);
  };

  const addQueryParam = () => {
    setQueryParams([...queryParams, { key: '', value: '' }]);
  };
  
  const removeQueryParam = (index: number) => {
    const newParams = queryParams.filter((_, i) => i !== index);
    setQueryParams(newParams.length > 0 ? newParams : [{ key: '', value: '' }]);
    updateQueryParamsString(newParams.length > 0 ? newParams : [{ key: '', value: '' }]);
  };
  
  const updateQueryParam = (index: number, field: 'key' | 'value', value: string, event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newParams = [...queryParams];
    newParams[index][field] = value;
    setQueryParams(newParams);
    updateQueryParamsString(newParams);
    
    // Check for "/" trigger in value field
    if (field === 'value' && event && event.target.selectionStart !== null) {
      const cursorPos = event.target.selectionStart;
      if (value[cursorPos - 1] === '/') {
        setVariableMenuAnchor({ element: event.target, type: 'query', index });
      } else {
        setVariableMenuAnchor(null);
      }
    }
  };
  
  const updateQueryParamsString = (params: Array<{key: string, value: string}>) => {
    const validParams = params.filter(p => p.key.trim() !== '');
    const queryString = validParams.map(p => `${encodeURIComponent(p.key)}=${encodeURIComponent(p.value)}`).join('&');
    setActionParameters({ ...actionParameters, queryParams: queryString });
  };
  
  const addBodyParam = () => {
    setBodyParams([...bodyParams, { key: '', value: '' }]);
  };
  
  const removeBodyParam = (index: number) => {
    const newParams = bodyParams.filter((_, i) => i !== index);
    setBodyParams(newParams.length > 0 ? newParams : [{ key: '', value: '' }]);
    updateBodyParamsString(newParams.length > 0 ? newParams : [{ key: '', value: '' }]);
  };
  
  const updateBodyParam = (index: number, field: 'key' | 'value', value: string, event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newParams = [...bodyParams];
    newParams[index][field] = value;
    setBodyParams(newParams);
    updateBodyParamsString(newParams);
    
    // Check for "/" trigger in value field
    if (field === 'value' && event && event.target.selectionStart !== null) {
      const cursorPos = event.target.selectionStart;
      if (value[cursorPos - 1] === '/') {
        setVariableMenuAnchor({ element: event.target, type: 'body', index });
      } else {
        setVariableMenuAnchor(null);
      }
    }
  };
  
  const updateBodyParamsString = (params: Array<{key: string, value: string}>) => {
    const validParams = params.filter(p => p.key.trim() !== '');
    const bodyObj: any = {};
    validParams.forEach(p => {
      bodyObj[p.key] = p.value;
    });
    setActionParameters({ ...actionParameters, body: JSON.stringify(bodyObj, null, 2) });
  };
  
  const addHeaderParam = () => {
    setHeaderParams([...headerParams, { key: '', value: '' }]);
  };
  
  const removeHeaderParam = (index: number) => {
    const newParams = headerParams.filter((_, i) => i !== index);
    setHeaderParams(newParams.length > 0 ? newParams : [{ key: 'Content-Type', value: 'application/json' }]);
    updateHeaderParamsString(newParams.length > 0 ? newParams : [{ key: 'Content-Type', value: 'application/json' }]);
  };
  
  const updateHeaderParam = (index: number, field: 'key' | 'value', value: string, event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newParams = [...headerParams];
    newParams[index][field] = value;
    setHeaderParams(newParams);
    updateHeaderParamsString(newParams);
    
    // Check for "/" trigger in value field
    if (field === 'value' && event && event.target.selectionStart !== null) {
      const cursorPos = event.target.selectionStart;
      if (value[cursorPos - 1] === '/') {
        setVariableMenuAnchor({ element: event.target, type: 'header', index });
      } else {
        setVariableMenuAnchor(null);
      }
    }
  };
  
  const updateHeaderParamsString = (params: Array<{key: string, value: string}>) => {
    const validParams = params.filter(p => p.key.trim() !== '');
    const headerObj: any = {};
    validParams.forEach(p => {
      headerObj[p.key] = p.value;
    });
    setActionParameters({ ...actionParameters, headers: JSON.stringify(headerObj, null, 2) });
  };

  const renderVariableMenu = () => (
    <Menu
      anchorEl={variableMenuAnchor?.element}
      open={Boolean(variableMenuAnchor)}
      onClose={() => setVariableMenuAnchor(null)}
      sx={{ maxHeight: 300 }}
    >
      <MenuItem onClick={() => insertVariable("Company Description")}>
        Company Description
      </MenuItem>
      <MenuItem onClick={() => insertVariable("Full Record")}>
        Full Record
      </MenuItem>
      {headers.map((header, i) => (
        <MenuItem
          key={i}
          onClick={() => insertVariable(header.field)}
        >
          {header.field}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <div>
      {/* Action Type section is hidden for HTTP requests - always defaults to update_rows */}

      <div style={{ marginTop: "16px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          HTTP Method
          <Tooltip title="GET: Retrieve data. POST: Send data to create resources. PUT/PATCH: Update existing resources. DELETE: Remove resources. Most APIs use POST for creating and GET for reading.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Select the HTTP method for your request.
        </Typography>
        <FormControl fullWidth style={{ marginTop: 8 }}>
          <InputLabel>Method</InputLabel>
          <Select
            label="Method"
            value={actionParameters.method || "POST"}
            onChange={(e) =>
              setActionParameters({
                ...actionParameters,
                method: e.target.value,
              })
            }
          >
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="PATCH">PATCH</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </Select>
        </FormControl>
      </div>

      <div style={{ marginTop: "16px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          URL
          <Tooltip title="The API endpoint to call. Use {{Column Name}} to insert row data into the URL. Example: https://api.example.com/users/{{userId}}">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Enter the target URL for your HTTP request. You can use row data with {'{{}} syntax.'}
        </Typography>
        <TextField
          fullWidth
          placeholder="https://api.example.com/users/{{id}}"
          variant="outlined"
          value={actionParameters.url || ""}
          onChange={(e) => handleUrlInputChange(e.target.value)}
          inputRef={urlRef}
          style={{ marginTop: "8px" }}
        />
      </div>

      <div style={{ marginTop: "16px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Query Parameters
          <Tooltip title="URL query string parameters (?key=value). These are appended to the URL. Use {{Column Name}} in values to insert row data dynamically.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Add query parameters as key-value pairs. You can use {'{{}} syntax'} for dynamic values.
        </Typography>
        {queryParams.map((param, index) => (
          <div key={index} style={{ display: "flex", gap: "8px", marginTop: "8px", alignItems: "center" }}>
            <TextField
              placeholder="Parameter name"
              variant="outlined"
              size="small"
              value={param.key}
              onChange={(e) => updateQueryParam(index, "key", e.target.value)}
              style={{ flex: 1 }}
            />
            <TextField
              placeholder="Parameter value (e.g., {{id}})"
              variant="outlined"
              size="small"
              value={param.value}
              onChange={(e) => updateQueryParam(index, "value", e.target.value, e)}
              style={{ flex: 2 }}
            />
            <IconButton
              onClick={() => removeQueryParam(index)}
              size="small"
              disabled={queryParams.length === 1}
            >
              <Delete />
            </IconButton>
          </div>
        ))}
        <Button
          startIcon={<Add />}
          onClick={addQueryParam}
          variant="outlined"
          size="small"
          style={{ marginTop: "8px" }}
        >
          Add Parameter
        </Button>
      </div>

      <div style={{ marginTop: "16px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Request Body (JSON)
          <Tooltip title="Data sent in the request body (for POST/PUT/PATCH). Each key-value pair becomes a JSON field. Use {{Column Name}} in values to insert row data.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Add request body fields as key-value pairs. You can use {'{{}} syntax'} for dynamic values.
        </Typography>
        {bodyParams.map((param, index) => (
          <div key={index} style={{ display: "flex", gap: "8px", marginTop: "8px", alignItems: "center" }}>
            <TextField
              placeholder="Field name"
              variant="outlined"
              size="small"
              value={param.key}
              onChange={(e) => updateBodyParam(index, "key", e.target.value)}
              style={{ flex: 1 }}
            />
            <TextField
              placeholder="Field value (e.g., {{firstName}} {{lastName}})"
              variant="outlined"
              size="small"
              value={param.value}
              onChange={(e) => updateBodyParam(index, "value", e.target.value, e)}
              style={{ flex: 2 }}
            />
            <IconButton
              onClick={() => removeBodyParam(index)}
              size="small"
              disabled={bodyParams.length === 1}
            >
              <Delete />
            </IconButton>
          </div>
        ))}
        <Button
          startIcon={<Add />}
          onClick={addBodyParam}
          variant="outlined"
          size="small"
          style={{ marginTop: "8px" }}
        >
          Add Field
        </Button>
      </div>

      <div style={{ marginTop: "16px" }}>
        <Typography variant="subtitle1" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          Headers
          <Tooltip title="HTTP headers sent with the request. Common headers: Authorization (API keys/tokens), Content-Type (usually application/json). Use {{Column Name}} for dynamic values.">
            <HelpOutline sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
          </Tooltip>
        </Typography>
        <Typography variant="body2">
          Add custom headers as key-value pairs. Common headers include Authorization, Content-Type, etc.
        </Typography>
        {headerParams.map((param, index) => (
          <div key={index} style={{ display: "flex", gap: "8px", marginTop: "8px", alignItems: "center" }}>
            <TextField
              placeholder="Header name (e.g., Authorization)"
              variant="outlined"
              size="small"
              value={param.key}
              onChange={(e) => updateHeaderParam(index, "key", e.target.value)}
              style={{ flex: 1 }}
            />
            <TextField
              placeholder="Header value (e.g., Bearer token)"
              variant="outlined"
              size="small"
              value={param.value}
              onChange={(e) => updateHeaderParam(index, "value", e.target.value, e)}
              style={{ flex: 2 }}
            />
            <IconButton
              onClick={() => removeHeaderParam(index)}
              size="small"
              disabled={headerParams.length === 1}
            >
              <Delete />
            </IconButton>
          </div>
        ))}
        <Button
          startIcon={<Add />}
          onClick={addHeaderParam}
          variant="outlined"
          size="small"
          style={{ marginTop: "8px" }}
        >
          Add Header
        </Button>
      </div>

      <Typography variant="caption" style={{ marginTop: "16px", display: "block" }}>
        Type{" "}
        <span
          style={{
            backgroundColor: "lightgray",
            padding: "2px 4px",
            borderRadius: "4px",
            color: "black",
          }}
        >
          /
        </span>{" "}
        in any value field to insert row variables, or use {'{{fieldName}}'} syntax directly
      </Typography>
      
      {renderVariableMenu()}
    </div>
  );
};